import {
  ArrowForward,
  ChevronRight,
  GridViewOutlined,
  InsertChartOutlined,
} from '@mui/icons-material'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

export type ResourceCardIcon = 'dashboard' | 'chart'

type ResourceCardBaseProps = {
  /** Resource title, truncated with an ellipsis; the full value shows on hover. */
  title: string
  /** The resource type, e.g. "Dashboard" or "Chart". */
  subtitle: string
  icon: ResourceCardIcon
}

export type ResourceCardProps = ResourceCardBaseProps &
  (
    | {
        variant: 'link'
        /** Omit to render a non-interactive card (e.g. an unsupported resource type). */
        onNavigate?: () => void
      }
    | {
        variant: 'accordion'
        children: React.ReactNode
        /** @default false */
        defaultExpanded?: boolean
      }
  )

const ICON_BY_TYPE: Record<ResourceCardIcon, React.ComponentType> = {
  dashboard: GridViewOutlined,
  chart: InsertChartOutlined,
}

/**
 * A resource reference card — icon tile + title/subtitle + a trailing
 * affordance that's either a link (navigates away) or an accordion toggle
 * (expands to reveal `children` in place). Not tied to any particular
 * resource type or surface.
 */
export const ResourceCard: React.FC<ResourceCardProps> = (props) => {
  const { title, subtitle, icon } = props
  const Icon = ICON_BY_TYPE[icon]
  const [expanded, setExpanded] = useState(
    props.variant === 'accordion' ? (props.defaultExpanded ?? false) : false,
  )

  const interactive = props.variant === 'accordion' || !!props.onNavigate

  const handleActivate = (): void => {
    if (props.variant === 'accordion') {
      setExpanded((prev) => !prev)
    } else {
      props.onNavigate?.()
    }
  }

  return (
    <Card>
      <Header
        as={interactive ? 'button' : 'div'}
        type={interactive ? 'button' : undefined}
        $interactive={interactive}
        onClick={interactive ? handleActivate : undefined}
        aria-expanded={props.variant === 'accordion' ? expanded : undefined}
      >
        <IconTile>
          <Icon />
        </IconTile>
        <TitleBlock>
          <Tooltip content={title}>
            <Title>{title}</Title>
          </Tooltip>
          <Subtitle>{subtitle}</Subtitle>
        </TitleBlock>
        {interactive &&
          (props.variant === 'accordion' ? (
            <TrailingIcon
              $expanded={expanded}
              data-testid="resource-card-chevron"
            >
              <ChevronRight />
            </TrailingIcon>
          ) : (
            <TrailingIcon>
              <ArrowForward />
            </TrailingIcon>
          ))}
      </Header>
      {props.variant === 'accordion' && expanded && (
        <Content>{props.children}</Content>
      )}
    </Card>
  )
}

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 12px;
`

const Header = styled.button<{ $interactive: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`

const IconTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f1eefc;
  color: #7b6fd6;

  svg {
    font-size: 20px;
  }
`

const TitleBlock = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
`

const Title = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1f2a44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Subtitle = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`

const TrailingIcon = styled.span<{ $expanded?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: #8c8c8c;
  transform: rotate(${({ $expanded }) => ($expanded ? 90 : 0)}deg);
  transition: transform 0.15s ease;

  svg {
    font-size: 20px;
  }
`

const Content = styled.div`
  min-width: 0;
`
